import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams, Link } from "react-router-dom"
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, X, ChevronLeft, ChevronRight } from "lucide-react"
import { SiteNavbar } from "../Navbar/SiteNavbar"
import { FooterSection } from "../Footer/FooterSection"
import { productDetails } from "./productDetailsData"
import "./ProductDetailsPage.css"

export function ProductDetailsPage() {
  const { t, i18n } = useTranslation()
  const { slug } = useParams()
  const product = productDetails[slug]
  const gallery = product?.gallery || []
  const isRTL = i18n.dir() === "rtl"

  const [lightboxIndex, setLightboxIndex] = useState(null)

  const closeLightbox = () => setLightboxIndex(null)
  const showPrev = useCallback((e) => {
    e?.stopPropagation()
    setLightboxIndex((i) => (i - 1 + gallery.length) % gallery.length)
  }, [gallery.length])
  const showNext = useCallback((e) => {
    e?.stopPropagation()
    setLightboxIndex((i) => (i + 1) % gallery.length)
  }, [gallery.length])

  useEffect(() => {
    if (lightboxIndex === null) return
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeLightbox()
      if (e.key === "ArrowLeft") (isRTL ? showNext : showPrev)()
      if (e.key === "ArrowRight") (isRTL ? showPrev : showNext)()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [lightboxIndex, gallery.length, isRTL, showNext, showPrev])

  if (!product) {
    return (
      <>
        <SiteNavbar />
        <main className="product-details product-details--empty">
          <div className="container">
            <h1>{t("Product not found")}</h1>
            <p>{t("We couldn't find the product you're looking for.")}</p>
            <Link to="/#products" className="btn-pill btn-pill--brand">
              {isRTL ? <ArrowRight size={14} /> : <ArrowLeft size={14} />}
              {t("Back to Products")}
            </Link>
          </div>
        </main>
        <FooterSection />
      </>
    )
  }

  const {
    title,
    brand,
    category,
    tagline,
    description,
    usages,
    features,
    specs,
    standards,
  } = product

  return (
    <>
      <SiteNavbar />
      <main className="product-details">
        {/* Hero */}
        <section className="product-hero">
          <div className="container product-hero__grid">
            <div className="product-hero__text">
              <Link to="/#products" className="product-hero__back">
                {isRTL ? <ArrowRight size={14} /> : <ArrowLeft size={14} />}
                {t("Back to Products")}
              </Link>
              <span className="eyebrow">{t(category)}</span>
              <h1 className="product-hero__title">{t(title)}</h1>
              <p className="product-hero__tagline">{t(tagline)}</p>

              <div className="product-hero__meta">
                <span className="product-hero__brand">
                  {t("Brand:")} <strong>{t(brand)}</strong>
                </span>
                {standards && standards.length > 0 && (
                  <div className="product-hero__badges">
                    {standards.map((s) => (
                      <span key={s} className="product-hero__badge">
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <a href="/contact" className="btn-pill btn-pill--brand product-hero__cta">
                {t("Send Us a Message")}
                <span className="btn-pill__arrow">
                  <ArrowUpRight size={14} />
                </span>
              </a>
            </div>

            {gallery && gallery.length > 0 && (
              <div
                className={`product-hero__media${
                  gallery.length === 1 ? " product-hero__media--single" : ""
                }`}
              >
                <div
                  className="product-hero__media-main"
                  onClick={() => setLightboxIndex(0)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && setLightboxIndex(0)}
                >
                  <img src={gallery[0]} alt={t(title)} />
                </div>
                {gallery.length > 1 && (
                  <div className="product-hero__media-sub">
                    {gallery.slice(1).map((src, i) => (
                      <div
                        className="product-hero__media-thumb"
                        key={src + i}
                        onClick={() => setLightboxIndex(i + 1)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && setLightboxIndex(i + 1)}
                      >
                        <img src={src} alt={`${t(title)} view ${i + 2}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Description */}
        <section className="product-section">
          <div className="container product-section__inner">
            <h2 className="product-section__title">{t("Overview")}</h2>
            <div className="product-description">
              {description.map((paragraph, i) => (
                <p key={i}>{t(paragraph)}</p>
              ))}
            </div>
          </div>
        </section>

        {/* Usages + Features */}
        {(usages || features) && (
          <section className="product-section product-section--muted">
            <div className="container product-section__inner">
              <div className="product-columns">
                {usages && (
                  <div className="product-column">
                    <h2 className="product-section__title">{t("Usages")}</h2>
                    <ul className="product-list">
                      {usages.map((item, i) => (
                        <li key={i}>
                          <Check size={16} />
                          <span>{t(item)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {features && (
                  <div className="product-column">
                    <h2 className="product-section__title">{t("Features")}</h2>
                    <ul className="product-list">
                      {features.map((item, i) => (
                        <li key={i}>
                          <Check size={16} />
                          <span>{t(item)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Specs table */}
        {specs && specs.length > 0 && (
          <section className="product-section">
            <div className="container product-section__inner">
              <h2 className="product-section__title">{t("Technical Specifications")}</h2>
              <div className="product-specs">
                {specs.map((spec) => (
                  <div key={spec.label} className="product-specs__row">
                    <span className="product-specs__label">{t(spec.label)}</span>
                    <span className="product-specs__value">{t(spec.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        <section className="product-section product-cta">
          <div className="container product-cta__inner">
            <h2>{t("Interested in {title}?", { title: t(title) })}</h2>
            <p>{t("Get in touch and our team will help you with pricing, lead times and installation.")}</p>
            <a href="/contact" className="btn-pill btn-pill--brand">
              {t("Send Us a Message")}
              <span className="btn-pill__arrow">
                <ArrowUpRight size={14} />
              </span>
            </a>
          </div>
        </section>
      </main>
      <FooterSection />

      {lightboxIndex !== null && gallery.length > 0 && (
        <div
          className={`lightbox${isRTL ? " lightbox--rtl" : ""}`}
          onClick={closeLightbox}
          dir={isRTL ? "rtl" : "ltr"}
        >
          <button className="lightbox__close" onClick={closeLightbox} aria-label={t("Close")}>
            <X size={22} />
          </button>

          {gallery.length > 1 && (
            <button
              className="lightbox__nav lightbox__nav--prev"
              onClick={showPrev}
              aria-label={t("Previous image")}
            >
              {isRTL ? <ChevronRight size={28} /> : <ChevronLeft size={28} />}
            </button>
          )}

          <img
            src={gallery[lightboxIndex]}
            alt={t(title)}
            className="lightbox__image"
            onClick={(e) => e.stopPropagation()}
          />

          {gallery.length > 1 && (
            <button
              className="lightbox__nav lightbox__nav--next"
              onClick={showNext}
              aria-label={t("Next image")}
            >
              {isRTL ? <ChevronLeft size={28} /> : <ChevronRight size={28} />}
            </button>
          )}

          <div className="lightbox__caption" onClick={(e) => e.stopPropagation()}>
            <span className="lightbox__title">{t(title)}</span>
            <span className="lightbox__counter" dir="ltr" aria-live="polite">
              {lightboxIndex + 1} / {gallery.length}
            </span>
          </div>
        </div>
      )}
    </>
  )
}